import React from 'react';
import type { ToolResultBlock, ToolUseBlock } from '../../../store/chatStore';
import { GenericTableRenderer } from './Table/GenericTableRenderer';
import { DetailsRenderer } from './Details/DetailsRenderer';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import { safeJsonParse as repairJsonParse } from '../../../../../utils/jsonRepairUtils';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useIsAdmin } from '../../../../onboarding-new/api';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

interface Props {
  result: ToolResultBlock;
  toolUse: ToolUseBlock;
}

interface TableContent {
  data: Record<string, unknown>[];
  meta?: {
    page: number;
    perPage: number;
    total: number;
  };
}

interface DetailsContent {
  data: Record<string, unknown>;
}

const safeJSONParse = (content: unknown): unknown => {
  if (typeof content !== 'string') {
    return content;
  }
  try {
    return repairJsonParse(content, 'tool result content');
  } catch (e) {
    console.warn('Failed to parse tool result content even with repair:', e);
    return content;
  }
};

const isTableData = (content: unknown): content is TableContent => {
  const parsedContent = safeJSONParse(content);
  if (!parsedContent || typeof parsedContent !== 'object') return false;
  const contentObj = parsedContent as TableContent;
  if (!Array.isArray(contentObj.data)) return false;
  if (contentObj.data.length === 0 || typeof contentObj.data[0] !== 'object')
    return false;
  return true;
};

const isDetailsData = (content: unknown): content is DetailsContent => {
  const parsedContent = safeJSONParse(content);
  if (!parsedContent || typeof parsedContent !== 'object') return false;
  const contentObj = parsedContent as Record<string, unknown>;
  return (
    'data' in contentObj &&
    typeof contentObj.data === 'object' &&
    contentObj.data !== null &&
    Object.keys(contentObj.data).length > 0
  );
};

const isJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

const getMoreOptions = (
  t: TFunction<'pleri/chat'>,
  toolUse: ToolUseBlock,
  isAdmin: boolean,
) => {
  const defaultOptions = [
    {
      label: <InformationCircleIcon className="h-5 w-5" />,
      message: 'Show me more information about this item.',
      tooltip: t('moreInfo'),
    },
  ];

  if (toolUse.name === 'listVulnerabilities') {
    if (isAdmin) {
      return [
        {
          label: t('info'),
          message: 'Show me more information about this item.',
        },
        { label: t('exempt'), message: 'Exempt this vulnerability.' },
      ];
    }
    return defaultOptions;
  }

  if (toolUse.name === 'listFindings' || toolUse.name === 'getFindingById') {
    if (isAdmin) {
      return [
        {
          label: t('info'),
          message: 'Show me more information about this item.',
        },
        { label: t('exempt'), message: 'Exempt this finding.' },
      ];
    }
    return defaultOptions;
  }

  if (toolUse.name === 'listRisks') {
    if (isAdmin) {
      return [
        {
          label: t('info'),
          message: 'Show me more information about this risk.',
        },
        { label: t('markAsAccepted'), message: 'Mark this risk as accepted.' },
        {
          label: t('markAsNotARisk'),
          message: 'Mark this risk as not a risk.',
        },
      ];
    }
    return defaultOptions;
  }

  return defaultOptions;
};

/** Minimal status pill — matches the pattern in DebugView. */
const StatusBadge: React.FC<{ status: 'success' | 'error' }> = ({ status }) => {
  const { t } = useTranslation('pleri/chat');
  if (status === 'success') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950/40 dark:text-green-400">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
        OK
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/40 dark:text-red-400">
      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
      {t('error')}
    </span>
  );
};

export const DefaultRenderer: React.FC<Props> = ({ result, toolUse }) => {
  const { t } = useTranslation('pleri/chat');
  const isAdmin = useIsAdmin();

  // ── Table / details ────────────────────────────────────────────────────────
  if (!result.isError && isTableData(result.content)) {
    return (
      <GenericTableRenderer
        result={result}
        toolUse={toolUse}
        moreOptionsOverride={getMoreOptions(t, toolUse, isAdmin)}
      />
    );
  }

  if (!result.isError && isDetailsData(result.content)) {
    return <DetailsRenderer result={result} toolUse={toolUse} />;
  }

  const contentText =
    typeof result.content === 'string'
      ? result.content
      : JSON.stringify(result.content, null, 2);

  // ── Success with markdown ──────────────────────────────────────────────────
  // Pure prose — no nested cards. The content IS the UI.
  if (
    !result.isError &&
    typeof result.content === 'string' &&
    !isJsonString(result.content)
  ) {
    return (
      <div className="flex flex-col">
        {/* Slim meta bar: tool name left, status right */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-2.5 dark:border-gray-800">
          <span className="text-xs font-medium tracking-wider text-gray-400 uppercase dark:text-gray-500">
            {result.toolName}
          </span>
          <StatusBadge status="success" />
        </div>

        {/* Markdown rendered as clean prose */}
        <div className="px-6 py-5">
          <div className="prose prose-sm dark:prose-invert prose-headings:font-semibold prose-headings:tracking-tight prose-p:text-gray-600 prose-p:leading-relaxed dark:prose-p:text-gray-400 max-w-none">
            <ChatMarkdown content={result.content} />
          </div>
        </div>
      </div>
    );
  }

  // ── Error or JSON content ──────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Meta bar */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500">
          {result.toolName}
        </span>
        <StatusBadge status={result.isError ? 'error' : 'success'} />
      </div>

      {/* Content — dark terminal block */}
      <pre className="overflow-x-auto rounded-lg bg-[#0d1117] p-4 font-mono text-xs leading-relaxed text-[#e6edf3]">
        {contentText}
      </pre>
    </div>
  );
};
